import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Sparkles,
  Heart,
  Sun,
  Flower2,
  Activity,
  Zap,
  Leaf,
  Moon,
  Video,
  Clock,
  Lock,
  Globe2,
  Calendar,
  HeartHandshake,
} from 'lucide-react';
import { mockData } from '../mock';

const SERVICE_META = {
  'pmos-support': { Icon: Flower2, accent: 'rose' },
  'pmdd-support': { Icon: Moon, accent: 'mauve' },
  'postpartum-support': { Icon: Heart, accent: 'blush' },
  'perimenopause-support': { Icon: Sun, accent: 'gold' },
  'hormone-optimization': { Icon: Sparkles, accent: 'gold' },
  'thyroid-support': { Icon: Zap, accent: 'sage' },
  'metabolic-stress-support': { Icon: Activity, accent: 'sage' },
  'menopause-support': { Icon: Leaf, accent: 'rose' },
};

const Services = () => {
  const getServiceLink = (serviceId) => {
    if (serviceId === 'perimenopause-support') return '/perimenopause';
    if (serviceId === 'pmos-support') return '/pmos';
    if (serviceId === 'menopause-support') return '/menopause';
    if (serviceId === 'pmdd-support') return '/pmdd';
    if (serviceId === 'postpartum-support') return '/postpartum';
    if (serviceId === 'thyroid-support') return '/thyroid';
    return '/intake';
  };

  const hasLandingPage = (serviceId) =>
    serviceId === 'perimenopause-support' ||
    serviceId === 'pmos-support' ||
    serviceId === 'menopause-support' ||
    serviceId === 'pmdd-support' ||
    serviceId === 'postpartum-support' ||
    serviceId === 'thyroid-support';

  return (
    <div className="page-services-v2">
      {/* Hero */}
      <section className="services-v2-hero">
        <div className="container">
          <div className="services-v2-hero-inner">
            <span className="services-v2-eyebrow">Personalized Hormone Care</span>
            <h1 className="services-v2-headline">
              Care designed around{' '}
              <span className="services-v2-headline-accent">your hormones</span>,
              not a checklist.
            </h1>
            <p className="services-v2-sub">
              Six focused programs, each built to address the root causes of how
              you actually feel, so the fix lasts longer than the symptom.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-v2-grid-section">
        <div className="container">
          <div className="services-v2-grid">
            {mockData.services
              .filter(
                (s) =>
                  s.id !== 'hormone-optimization' &&
                  s.id !== 'metabolic-stress-support'
              )
              .map((service) => {
              const meta = SERVICE_META[service.id] || { Icon: Sparkles, accent: 'gold' };
              const { Icon, accent } = meta;
              const featured = hasLandingPage(service.id);

              return (
                <article
                  key={service.id}
                  className={`services-v2-card accent-${accent} ${featured ? 'is-featured' : ''}`}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="services-v2-card-top">
                    <div className="services-v2-icon-wrap">
                      <Icon size={22} strokeWidth={1.6} />
                    </div>
                  </div>

                  <h2 className="services-v2-card-title">{service.title}</h2>
                  <p className="services-v2-card-desc">{service.description}</p>

                  <div className="services-v2-section">
                    <h4 className="services-v2-section-label">Common Symptoms</h4>
                    <div className="services-v2-tags">
                      {service.symptoms.map((symptom, index) => (
                        <span key={index} className="services-v2-tag">
                          {symptom}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="services-v2-section">
                    <h4 className="services-v2-section-label">What's Included</h4>
                    <ul className="services-v2-included">
                      {service.included.map((item, index) => (
                        <li key={index}>
                          <Check size={15} strokeWidth={2.5} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={getServiceLink(service.id)}
                    className="services-v2-cta"
                    data-testid={`service-cta-${service.id}`}
                  >
                    {featured ? 'Learn More' : 'Start Your Journey'}
                    <ArrowRight size={16} />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Telehealth advantages */}
      <section className="services-v2-telehealth" data-testid="services-telehealth">
        <div className="container">
          <div className="services-v2-telehealth-head">
            <span className="services-v2-eyebrow">Why Telehealth</span>
            <h2>Care that actually fits a real woman's day</h2>
            <p>
              Hormone health is not a one-visit fix. It is an ongoing conversation. Telehealth makes that conversation possible without the friction of clinics built around someone else's schedule.
            </p>
          </div>

          <div className="services-v2-telehealth-grid">
            <article className="services-v2-telehealth-card" data-testid="telehealth-advantage-time">
              <div className="services-v2-telehealth-icon"><Clock size={22} strokeWidth={1.5} /></div>
              <h3>No half-day off work</h3>
              <p>A 30-minute video visit from your couch beats two hours of traffic, parking, waiting rooms, and rescheduling.</p>
            </article>

            <article className="services-v2-telehealth-card" data-testid="telehealth-advantage-privacy">
              <div className="services-v2-telehealth-icon"><Lock size={22} strokeWidth={1.5} /></div>
              <h3>Privacy by default</h3>
              <p>Talk openly about sleep, intimacy, mood, and weight without sitting in a public lobby or worrying who you might run into.</p>
            </article>

            <article className="services-v2-telehealth-card" data-testid="telehealth-advantage-access">
              <div className="services-v2-telehealth-icon"><Globe2 size={22} strokeWidth={1.5} /></div>
              <h3>Specialist access anywhere in Utah</h3>
              <p>Most local clinics treat menopause as a footnote. With telehealth, you get a clinician who specializes in this, no matter where in the state you live.</p>
            </article>

            <article className="services-v2-telehealth-card" data-testid="telehealth-advantage-followup">
              <div className="services-v2-telehealth-icon"><Calendar size={22} strokeWidth={1.5} /></div>
              <h3>Easy follow-up, not "see you in 6 months"</h3>
              <p>Quick check-ins to fine-tune your dose, review labs, or adjust your plan. The right care is iterative, and we make iteration painless.</p>
            </article>

            <article className="services-v2-telehealth-card" data-testid="telehealth-advantage-secure">
              <div className="services-v2-telehealth-icon"><Video size={22} strokeWidth={1.5} /></div>
              <h3>HIPAA-secure video visits</h3>
              <p>Encrypted video on a secure platform, with notes and labs in your private patient portal. Your data is protected end-to-end.</p>
            </article>

            <article className="services-v2-telehealth-card" data-testid="telehealth-advantage-relationship">
              <div className="services-v2-telehealth-icon"><HeartHandshake size={22} strokeWidth={1.5} /></div>
              <h3>A real relationship with one clinician</h3>
              <p>Not a different rotating provider every visit. You build trust, your clinician learns your history, and your plan compounds in quality over time.</p>
            </article>
          </div>

          {/* Comparison table - telehealth vs in-person hormone care */}
          <div className="services-v2-compare" data-testid="services-compare">
            <h3 className="services-v2-compare-title">Telehealth vs in-person hormone care</h3>
            <div className="services-v2-compare-table">
              <div className="services-v2-compare-row services-v2-compare-head">
                <div></div>
                <div>Glow Health Telehealth</div>
                <div>Typical In-Person Visit</div>
              </div>
              <div className="services-v2-compare-row">
                <div className="services-v2-compare-label">Time per visit</div>
                <div className="services-v2-compare-yes">30 to 45 focused minutes on you</div>
                <div className="services-v2-compare-no">10 to 15 minutes, often rushed</div>
              </div>
              <div className="services-v2-compare-row">
                <div className="services-v2-compare-label">Travel + waiting room</div>
                <div className="services-v2-compare-yes">Zero. Visit from home, work, or your car</div>
                <div className="services-v2-compare-no">1 to 2 hours of overhead per visit</div>
              </div>
              <div className="services-v2-compare-row">
                <div className="services-v2-compare-label">Specialty focus</div>
                <div className="services-v2-compare-yes">Perimenopause, menopause, hormone health</div>
                <div className="services-v2-compare-no">General primary care or OB/GYN with broad load</div>
              </div>
              <div className="services-v2-compare-row">
                <div className="services-v2-compare-label">Lab work</div>
                <div className="services-v2-compare-yes">Sent to a local lab near you, results in your portal</div>
                <div className="services-v2-compare-no">Often requires a separate appointment</div>
              </div>
              <div className="services-v2-compare-row">
                <div className="services-v2-compare-label">Follow-up cadence</div>
                <div className="services-v2-compare-yes">Quick check-ins as needed, included in your plan</div>
                <div className="services-v2-compare-no">"See you at the next annual" by default</div>
              </div>
              <div className="services-v2-compare-row services-v2-compare-row-full">
                <div className="services-v2-compare-label">When in-person makes sense</div>
                <div className="services-v2-compare-full" data-testid="services-compare-when-in-person">
                  Some pelvic exams, IUD changes, or imaging still need a clinic visit. We will tell you honestly when telehealth is not the right fit and refer you locally.
                </div>
              </div>
            </div>
            <p className="services-v2-compare-footnote">
              Telehealth is not a workaround. For ongoing hormone care, it is genuinely the better format for most women. We use it because it lets us do the job well, not because it is convenient for us.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="services-v2-cta-banner">
        <div className="container">
          <div className="services-v2-cta-inner">
            <div className="services-v2-cta-text">
              <h2>Not sure which program fits?</h2>
              <p>
                Book a free 15-minute consultation and we'll help you find the
                right path, no pressure, no commitment.
              </p>
              <p className="services-v2-glp-note">
                <strong>Plus:</strong> GLP-1 weight-loss support is available alongside any program when clinically appropriate.
              </p>
            </div>
            <Link to="/book" className="services-v2-cta-btn" data-testid="services-bottom-cta">
              Book Your Free Consultation
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
